import axios from 'axios';
import utility from "../utils/utility";
import { createPkcePair, generateState, getCodes } from './pkce.js';
import IdentityVerificationDTO from "../models/IdentityVerificationDTO";
import { VALIDATE_OTP_SUCCESS } from '../Redux/actions/actionTypes.js';
import apiClient from '../utils/apiClient.js';
 
class EsignetClient {
  constructor(config = {}) {
    const t = (v) => (typeof v === 'string' ? v.trim() : v);
    this.config = {
      ...config,
      // Prefer explicit eSignet vars; fall back to generic OIDC issuer if provided
      issuer: (config.issuer) || (process.env.REACT_APP_ESIGNET_ISSUER) || (process.env.REACT_APP_OIDC_ISSUER),
      esignetUrl: (config.esignetUrl) || (process.env.REACT_APP_ESIGNET_URL) || (process.env.REACT_APP_ESIGNET_ISSUER) || (process.env.REACT_APP_OIDC_ISSUER),
      clientId: (config.clientId) || (process.env.REACT_APP_ESIGNET_CLIENT_ID),
      redirectUri: (config.redirectUri) || (process.env.REACT_APP_AUTH_CALLBACK_URL) ,
      backendUrl: (config.backendUrl) || (process.env.REACT_APP_RESIDENT_PORTAL_MIDDLEWARE_HOST),
      scope: (config.scope) || 'openid profile email',
      responseMode: (config.responseMode) || 'query',
      prompt: (config.prompt), // e.g. 'login' | 'consent' | 'select_account'
      acrValues: (config.AUTH_MODALITY) || (process.env.AUTH_MODALITY) || 'mosip:idp:acr:generated-code mosip:idp:acr:linked-wallet mosip:idp:acr:biometrics', // e.g. 'mosip:idp:acr:static-code' etc if required by your IdP policy
      uiLocales: (config.uiLocales), // e.g. 'en fr'
  // Optional OIDC nonce (some providers recommend even for code flow)
      nonce: (config.nonce) || (process.env.REACT_APP_OIDC_NONCE),
      aud: (config.aud) || (process.env.REACT_APP_AUD_URL),
      clientAssertionType: (config.clientAssertionType) || (process.env.REACT_APP_ESIGNET_URN),
      jwk: (config.jwk) || (process.env.REACT_APP_ESIGNET_JWK),
      kid: (config.kid) || (process.env.REACT_APP_ESIGNET_KID),
      privateKey: (config.privateKey) || (process.env.REACT_APP_ESIGNET_PRIVATE_KEY),
      userinfoPrivateKey: (config.userinfoPrivateKey) || (process.env.REACT_APP_ESIGNET_USERINFO_PRIVATE_KEY),
      // Optional explicit authorize endpoint overrides
      authorizeUrl: (config.authorizeUrl) || (process.env.REACT_APP_ESIGNET_AUTHORIZE_URL),
      authorizePath: (config.authorizePath) || (process.env.REACT_APP_ESIGNET_AUTHORIZE_PATH),
      claims: process.env.REACT_APP_ESIGNET_LOGIN_FRAGMENT_JSON,
      // UI options
      showRedirectOverlay: (config.showRedirectOverlay ?? false),
    };
    this._tokens = null;
    this._user = null;
    this._roles = [];
  }

  validateConfig() {
    const hasIssuer = Boolean(this.config.esignetUrl || this.config.issuer);
    const missing = [];
    if (!hasIssuer) missing.push('issuer|esignetUrl');
    if (!this.config.clientId) missing.push('clientId');
    if (!this.config.redirectUri) missing.push('redirectUri');
    if (!this.config.backendUrl) missing.push('backendUrl');
    if (missing.length) {
      const snapshot = {
        issuer: this.config.issuer,
        esignetUrl: this.config.esignetUrl,
        clientId: this.config.clientId,
        redirectUri: this.config.redirectUri,
        backendUrl: this.config.backendUrl,
        scope: this.config.scope,
        responseMode: this.config.responseMode,
      };
      console.warn('EsignetClient config (debug):', snapshot);
      throw new Error(`Missing required eSignet OIDC configuration: ${missing.join(', ')}`);
    }
    if (!this.config.issuer && this.config.esignetUrl) {
      this.config.issuer = this.config.esignetUrl;
    }
  }


  showProgressOverlay(message = 'Processing...') {
    let el = document.getElementById('esignet-progress-overlay');
      if (!el) {
        el = document.createElement('div');
        el.id = 'esignet-progress-overlay';
        el.style.cssText = [
          'position:fixed','inset:0','z-index:9999',
          'display:flex','align-items:center','justify-content:center',
          'background:rgba(0,0,0,0.5)','backdrop-filter:blur(2px)'
        ].join(';');
        const box = document.createElement('div');
        box.id = 'esignet-progress-box';
        box.style.cssText = [
          'background:#fff','padding:16px 24px','border-radius:12px',
          'box-shadow:0 10px 25px rgba(0,0,0,0.2)','font-family:sans-serif',
          'display:flex','gap:12px','align-items:center'
        ].join(';');
        const spinner = document.createElement('div');
        spinner.id = 'esignet-progress-spinner';
        spinner.style.cssText = [
          'width:24px','height:24px','border-radius:50%',
          'border:3px solid #e5e7eb','border-top-color:#0f4c81',
          'animation:esignet-spin 1s linear infinite'
        ].join(';');
        const text = document.createElement('div');
        text.id = 'esignet-progress-text';
        text.textContent = message;
        box.appendChild(spinner);
        box.appendChild(text);
        el.appendChild(box);
        const style = document.createElement('style');
        style.textContent = '@keyframes esignet-spin{to{transform:rotate(360deg)}}';
        el.appendChild(style);
        document.body.appendChild(el);
      } else {
        const text = document.getElementById('esignet-progress-text');
        if (text) text.textContent = message;
      }
  }

  hideProgressOverlay() {
    const el = document.getElementById('esignet-progress-overlay');
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  emitProgress(status, message) {
    try {
      sessionStorage.setItem('esignet_progress', status);
      window.dispatchEvent(new CustomEvent('esignet-progress', { detail: { status, message } }));
    } catch {}
  }


  async generateSignInUrl() {
    const { codeVerifier, codeChallenge ,nonce, state} = await createPkcePair();
    sessionStorage.setItem('esignet_code_verifier',codeVerifier)
    const params = new URLSearchParams({
        client_id: this.config.clientId,
        redirect_uri: this.config.redirectUri,
        response_type: "code",
        scope: "openid profile email",
        acr_values: this.config.AUTH_MODALITY || process.env.AUTH_MODALITY || "mosip:idp:acr:generated-code",
        
        claims: '{"userinfo":{"individual_id":{"essential":true}}}',
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        display: "page",
        nonce: nonce,
        state: state,
        claims_locales: "en am",
        ui_locales: "en",
    });

    return `${
        this.config.aud
    }?${params.toString()}`;
};

  async login() {
    const authorizeUrl = await this.generateSignInUrl();
    localStorage.removeItem('xcode')
    localStorage.removeItem('xstate')
    if (this.config.showRedirectOverlay) {
      this.emitProgress('redirect', 'Redirecting to eSignet...');
      this.showProgressOverlay('Redirecting to eSignet...');
    }
    window.location.assign(authorizeUrl);
  }

  async logout() {
    sessionStorage.removeItem('esignet_code_verifier');
    // sessionStorage.removeItem('residentPortalToken');
  }

  async handleCallback(code) {    
      const { codeVerifier, codeChallenge, nonce, state } = getCodes();

      try {
        this.emitProgress('callback', 'Wait please...');
        this.showProgressOverlay('Wait please...');
       
        const payload = {
          code,
          code_verifier: codeVerifier,
          redirect_uri: this.config.redirectUri,
          client_id: this.config.clientId,
          nonce,
          state
        };

        const base = this.config.backendUrl || '';
        const tokenUrl = `${base}/esignet/exchangeAutheCode`;
        const usrUrl = `${base}/esignet/exchangeResident`;
        
        const tokenResponse = await apiClient.post(tokenUrl, payload, {
            headers: { 'Content-Type': 'application/json' },
        });

        const accessToken =  tokenResponse?.data;
        const userResponse = await apiClient.post(usrUrl, {
            headers: { 'Content-Type': 'application/json' },
        });
        this.emitProgress('done', 'Signed in successfully');
        this.hideProgressOverlay();
        return userResponse;
      } catch (err) {
        this.emitProgress('error', 'eSignet sign-in failed');
        this.showProgressOverlay('eSignet sign-in failed');
        utility(err);
        this.hideProgressOverlay();
      }
  }
}

const esignetClient = new EsignetClient();

export { EsignetClient };
export default esignetClient;
